/**
 * Low-level API client functions for RFP
 *
 * These functions handle HTTP requests to the BTX service for RFP-related data.
 */

import { AxiosInstance } from "axios";
import { logger } from "@platform/browser-logger";

export interface RfpCoverageResponse {
  id: string;
  type: string;
  productType?: string;
  rfpId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Lists all coverages for an RFP using the lightweight coverages endpoint
 *
 * Uses BTX endpoint: GET /btx/v1/rfps/{rfpId}/coverages
 *
 * This provides the canonical source of truth for which coverages exist
 * for an RFP, with their IDs and types. Use this to map coverage types
 * to UUIDs when managing benefit class assignments.
 *
 * @param rfpId - The RFP ID to fetch coverages for
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to array of coverages
 *
 * @example
 * ```ts
 * const coverages = await listRfpCoverages("rfp-123", axiosInstance);
 * const coverageMap = new Map(coverages.map(c => [c.type, c.id]));
 * const dentalCoverageId = coverageMap.get("dental");
 * ```
 */
export const listRfpCoverages = async (
  rfpId: string,
  axiosInstance?: AxiosInstance,
): Promise<RfpCoverageResponse[]> => {
  if (!axiosInstance) {
    throw new Error("Axios instance required for RFP API");
  }

  logger.debug("[BTX API] Listing RFP coverages:", { rfpId });

  const response = await axiosInstance.get<RfpCoverageResponse[]>(
    `/btx/v1/rfps/${rfpId}/coverages`,
  );

  logger.debug("[BTX API] Listed RFP coverages:", {
    rfpId,
    count: response.data.length,
  });

  return response.data;
};
